import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { startWeeklyTracking } from '../../lib/idle/weekly';
import { requirePremium } from '../../lib/premium';

import { Icon } from './Icons';
import GemsPanel from './GemsPanel';
import HelpersPanel from './HelpersPanel';
import Styles from './Sidebar.module.css';

// Kept so older pages that import these keep compiling
export { Icon } from './Icons';

// Practice is the Flashcards stage and the Scales page, so it stays lit on both.
export const NAV_ITEMS = [
  { label: 'Practice', icon: 'practice', to: '/', paths: ['/', '/scales'] },
  {
    label: 'Cosmetics',
    icon: 'cosmetics',
    to: '/cosmetics',
    // Free players stay where they are and get the Premium pricing modal instead.
    premiumReason: 'Cosmetics are a Premium feature.',
  },
  { label: 'Leaderboard', icon: 'leaderboard', to: '/leaderboard' },
  { label: 'Achievements', icon: 'achievements', to: '/achievements' },
];

export const getUserDisplay = (data, user) => ({
  name: data.username || data.displayName || (user && user.displayName) || 'Player',
  sub: `${data.membershipStatus || 'Basic'} member`,
});

const TABS = [
  { id: 'helpers', label: 'Store', icon: 'helpers' },
  { id: 'gems', label: 'Gem upgrades', icon: 'gem' },
];

const isOn = (item, pathname) =>
  item.paths
    ? item.paths.some((p) => (p === '/' ? pathname === '/' : pathname.startsWith(p)))
    : pathname === item.to || pathname.startsWith(`${item.to}/`);

const Sidebar = () => {
  const [tab, setTab] = useState('helpers');
  const { pathname } = useLocation();

  // Counts every flashcard solved toward this week's leaderboard, whichever
  // tab is open (the sidebar is always on screen).
  useEffect(() => startWeeklyTracking(), []);

  return (
    <aside className={Styles.sidebar}>
      <div className={Styles.wordmark}>Brassflows</div>

      <div className={Styles.rail}>
        <nav className={Styles.links} aria-label="Pages">
          {NAV_ITEMS.map((item) => {
            const on = isOn(item, pathname);
            return (
              <Link
                key={item.to}
                to={item.to}
                aria-label={item.label}
                aria-current={on ? 'page' : undefined}
                title={item.label}
                onClick={(event) => {
                  if (item.premiumReason && !requirePremium(item.premiumReason)) event.preventDefault();
                }}
                className={`${Styles.link} ${on ? Styles.linkOn : ''}`}
              >
                <Icon name={item.icon} className={Styles.tabIcon} />
              </Link>
            );
          })}
        </nav>

        <div className={Styles.tabs} role="tablist" aria-label="Band panel">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              aria-label={t.label}
              title={t.label}
              className={`${Styles.tab} ${tab === t.id ? Styles.tabOn : ''}`}
              onClick={() => setTab(t.id)}
            >
              <Icon name={t.icon} className={Styles.tabIcon} />
            </button>
          ))}
        </div>
      </div>

      <div className={Styles.body} role="tabpanel">
        {tab === 'helpers' && <HelpersPanel />}
        {tab === 'gems' && <GemsPanel />}
      </div>
    </aside>
  );
};

export default Sidebar;